/**
 * Sidebar Component
 * Collapsible left navigation sidebar with menu items
 */

import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Home, PlusSquare, BarChart3, Lightbulb, LogOut, ChevronLeft, ChevronRight, User } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { useAnalysisDialogStore } from '@/stores/analysisDialogStore';
import { useAnalystDialogStore } from '@/stores/analystDialogStore';
import { cn } from '@/lib/utils';
import eclouLogo from '@/assets/logo_eclou_2025.png';
import { SidebarCredits } from './SidebarCredits';

export interface SidebarProps {
  /** Additional class names */
  className?: string;
}

interface NavItem {
  to?: string;
  action?: string;
  labelKey: string;
  icon: React.ReactNode;
}

/**
 * Navigation items for the sidebar
 */
const navItems: NavItem[] = [
  { to: '/dashboard', labelKey: 'sidebar.dashboard', icon: <Home className="w-4 h-4" /> },
  { action: 'openAnalysisDialog', labelKey: 'sidebar.newAnalysis', icon: <PlusSquare className="w-4 h-4" /> },
  { to: '/analysis-results', labelKey: 'sidebar.myAnalyses', icon: <BarChart3 className="w-4 h-4" /> },
  { action: 'openAnalystDialog', labelKey: 'sidebar.analyst', icon: <Lightbulb className="w-4 h-4" /> },
  { to: '/profile', labelKey: 'sidebar.profile', icon: <User className="w-4 h-4" /> },
];

/**
 * Collapsible sidebar navigation component
 */
export function Sidebar({ className }: SidebarProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const logout = useAuthStore((state) => state.logout);
  const openAnalysisDialog = useAnalysisDialogStore((s) => s.open);
  const openAnalystDialog = useAnalystDialogStore((s) => s.open);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const nonResultsPaths = ['/analysis/multichannel-kampagne'];

  const isActive = (path: string) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard' || location.pathname === '/';
    }
    // Mark "Results" as active when viewing a specific analysis (/analysis/:id)
    if (path === '/analysis-results') {
      return (location.pathname.startsWith('/analysis-results') ||
        location.pathname.startsWith('/analysis/')) &&
        !nonResultsPaths.some((p) => location.pathname.startsWith(p));
    }
    return location.pathname.startsWith(path);
  };

  return (
    <aside
      className={cn(
        'min-h-screen',
        'bg-[var(--color-white)]',
        'border-r border-gray-100',
        'flex flex-col',
        'transition-all duration-300 ease-in-out',
        isCollapsed ? 'w-16' : 'w-48',
        className
      )}
    >
      {/* Logo */}
      <div className={cn(
        'p-4 pb-6',
        isCollapsed ? 'flex justify-center' : 'text-center'
      )}>
        <Link to="/dashboard" className="inline-block">
          {isCollapsed ? (
            <img src={eclouLogo} alt="eclou" className="h-6 w-6 object-cover object-left" />
          ) : (
            <img src={eclouLogo} alt="eclou" className="h-7" />
          )}
        </Link>
        {!isCollapsed && (
          <span className="block text-[10px] font-semibold tracking-widest uppercase text-gray-400 mt-1">
            Beta
          </span>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3" role="navigation" aria-label={t('sidebar.mainNavigation')}>
        <ul className="space-y-0.5">
          {navItems.map((item) => {
            const key = item.to ?? item.action!;
            if (item.action) {
              return (
                <li key={key}>
                  <button
                    onClick={
                      item.action === 'openAnalysisDialog' ? openAnalysisDialog :
                      item.action === 'openAnalystDialog' ? openAnalystDialog :
                      undefined
                    }
                    data-track={`nav_${item.action === 'openAnalysisDialog' ? 'new_analysis' : item.action === 'openAnalystDialog' ? 'analyst' : item.action}`}
                    className={cn(
                      'w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg transition-colors text-sm cursor-pointer',
                      'text-[var(--color-charcoal)]',
                      isCollapsed && 'justify-center px-0',
                      'hover:bg-gray-50'
                    )}
                    title={isCollapsed ? t(item.labelKey) : undefined}
                  >
                    <span className="text-gray-500">{item.icon}</span>
                    {!isCollapsed && <span>{t(item.labelKey)}</span>}
                  </button>
                </li>
              );
            }
            return (
              <li key={key}>
                <Link
                  to={item.to!}
                  data-track={`nav_${item.to!.replace(/^\//, '').replace(/[-/]/g, '_') || 'dashboard'}`}
                  className={cn(
                    'flex items-center gap-2.5 px-2.5 py-2 rounded-lg transition-colors text-sm',
                    'text-[var(--color-charcoal)]',
                    isCollapsed && 'justify-center px-0',
                    isActive(item.to!)
                      ? 'bg-gray-100 font-medium'
                      : 'hover:bg-gray-50'
                  )}
                  title={isCollapsed ? t(item.labelKey) : undefined}
                >
                  <span className={cn(
                    isActive(item.to!) ? 'text-[var(--color-primary-start)]' : 'text-gray-500'
                  )}>
                    {item.icon}
                  </span>
                  {!isCollapsed && <span>{t(item.labelKey)}</span>}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Credits */}
      <div className="px-3 pb-2">
        <SidebarCredits isCollapsed={isCollapsed} />
      </div>

      {/* Bottom section - Collapse toggle and Logout button */}
      <div className="p-3 border-t border-gray-100 space-y-1">
        {/* Collapse toggle */}
        <button
          onClick={toggleCollapse}
          className={cn(
            'w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm text-gray-500 hover:bg-gray-50 hover:text-[var(--color-charcoal)] transition-colors cursor-pointer',
            isCollapsed && 'justify-center px-0'
          )}
          aria-label={isCollapsed ? t('sidebar.expand') : t('sidebar.collapse')}
          title={isCollapsed ? t('sidebar.expand') : undefined}
        >
          {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          {!isCollapsed && <span>{t('sidebar.collapse')}</span>}
        </button>

        {/* Logout button */}
        <button
          onClick={handleLogout}
          data-track="nav_logout"
          className={cn(
            'w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm text-gray-500 hover:bg-gray-50 hover:text-[var(--color-charcoal)] transition-colors cursor-pointer',
            isCollapsed && 'justify-center px-0'
          )}
          title={isCollapsed ? t('common.logout') : undefined}
        >
          <LogOut className="w-4 h-4" />
          {!isCollapsed && <span>{t('common.logout')}</span>}
        </button>
      </div>
    </aside>
  );
}
